import { useAuth0 } from "@auth0/auth0-react";
import { Menu, PowerSettingsNew } from "@mui/icons-material";
import { AppBar, Box, Container, Grid, IconButton, Toolbar, Typography } from "@mui/material";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, useRouter } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import bg from "@/assets/bg.jpg";
import mahanaLogo from "@/assets/LOGO2.svg";
import ErrorCard from "@/components/ErrorCard";
import FullPageSpinner from "@/components/FullPageSpinner";
import Navigation from "@/components/Navigation.tsx";
import type { QueryOptionsFactory } from "@/queries";
import { adminRole } from "@/utils/roles.ts";

export type RootRouterContext = {
    queryClient: QueryClient;
    qof: QueryOptionsFactory;
};

const Root = (): ReactNode => {
    const { user, logout } = useAuth0();
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const { state } = useRouter();
    const isGuest = state.location.pathname.startsWith("/guest");

    return (
        <>
            <AppBar
                position="static"
                sx={{
                    background: "linear-gradient(135deg, #28638c 0%, #3a7a8c 100%)",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
            >
                <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                        {!isGuest && (
                            <IconButton
                                color="inherit"
                                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                                sx={{
                                    backgroundColor: "rgba(255,255,255,0.1)",
                                    "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                                    display: { xs: "flex", md: "none" },
                                }}
                            >
                                <Menu />
                            </IconButton>
                        )}
                        <img
                            src={mahanaLogo}
                            alt="Mahana Logo"
                            style={{ height: "32px", width: "auto" }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="h5"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    background: "linear-gradient(45deg, #fbbf24, #f59e0b)",
                                    backgroundClip: "text",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                                    fontSize: { xs: ".8rem", sm: "1.25rem", md: "2rem" },
                                    lineHeight: { xs: 1.2, md: 1.3 },
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: { xs: "nowrap", sm: "normal" },
                                }}
                            >
                                Guest Registration System
                            </Typography>
                            {user?.globalRoles.includes(adminRole) && (
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        color: "#fbbf24",
                                        fontWeight: 500,
                                        fontStyle: "italic",
                                        fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                                        lineHeight: 1.2,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Administration Area
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {user && (
                        <PowerSettingsNew
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                                void logout({
                                    logoutParams: {
                                        returnTo: window.origin,
                                    },
                                });
                            }}
                        />
                    )}
                </Toolbar>
            </AppBar>
            <Grid
                container
                spacing={2}
                sx={{
                    py: 2,
                    justifyContent: "center",
                    backgroundImage: `url(${bg})`,
                    backgroundRepeat: "repeat",
                    backgroundSize: "auto",
                    minHeight: "100vh",
                    padding: 1,
                }}
            >
                {!isGuest && (
                    <Grid size="auto" sx={{ display: "flex", justifyContent: "space-around" }}>
                        <Navigation isDrawerOpen={isDrawerOpen} setIsDrawerOpen={setIsDrawerOpen} />
                    </Grid>
                )}
                <Grid size="grow">
                    <Outlet />
                </Grid>
            </Grid>
        </>
    );
};

export const Route = createRootRouteWithContext<RootRouterContext>()({
    component: Root,
    pendingComponent: FullPageSpinner,
    errorComponent: ({ error }) => (
        <Container maxWidth="md" sx={{ my: 2 }}>
            <ErrorCard error={error} />
        </Container>
    ),
});
